import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  IconButton,
  Chip,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import PageHeader from "../components/PageHeader";
import VenueCover from "../components/venues/VenueCover";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import CustomTextField from "../components/CustomTextField";
import AddressWithMapField from "../components/map/AddressWithMapField";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";
import {
  updateBusiness,
  getAllBusinesses,
  deleteVenue,
} from "../services/VenueService";
import {
  updateBranch,
  getAllBranches,
  deleteBranch,
  getAllBusinessTypes,
} from "../services/BranchesService";
import { toast } from "react-toastify";

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

const venueFields: Field[] = [
  { type: "text" as const, name: "name", label: "Venue Name" },
  {
    type: "number" as const,
    name: "orderNumber",
    label: "Order Number",
  },
  { type: "file" as const, name: "banner", label: "Venue Banner" },
  { type: "text" as const, name: "overLay", label: "Overlay Text" },
  { type: "text" as const, name: "insta_url", label: "Instagram URL" },
  {
    name: "address",
    label: "Address",
    colSpan: 2,
    customRender: (
      value: any,
      onChange: (v: any) => void,
      helpers?: {
        setFieldValue: (name: string, value: any) => void;
        values?: Record<string, any>;
      }
    ) => {
      return (
        <AddressWithMapField value={value} onChange={onChange} helpers={helpers} />
      );
    },
  },
];

let branchFields: Field[] = [];
const deleteFields: Field[] = [
  {
    name: "name",
    label: "Name",
    required: true,
    customRender: (value: any) => (
      <CustomTextField label="Name" value={value || ""} disabled fullWidth />
    ),
  },
  { type: "password", name: "password", label: "Password", required: true },
];

const EditVenues: React.FC = () => {
  const [venues, setVenues] = useState<any[]>([]);
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [selectedVenueIdx, setSelectedVenueIdx] = useState<number | null>(null);
  const [selectedBranchIdx, setSelectedBranchIdx] = useState<number | null>(
    null
  );
  const [venueTypes, setVenueTypes] = useState<{ id: string; name: string }[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { type: "venue"; vIdx: number }
    | { type: "branch"; vIdx: number; bIdx: number }
    | null
  >(null);
  const [businessTypes, setBusinessTypes] = useState<
    { id: string; name: string }[]
  >([]);

  const loadVenuesWithBranches = React.useCallback(async () => {
    try {
      setLoading(true);
      const allBusinesses = await getAllBusinesses();
      if (allBusinesses && Array.isArray(allBusinesses)) {
        // DELETE /business/delete/:id is a soft delete, so inactive rows keep
        // coming back from the list endpoint
        const response = allBusinesses.filter((v: any) => v?.active !== false);
        const types = response.map((venue: any) => ({
          id: venue.id || venue._id || venue.venue_id || String(venue.name),
          name: venue.name || "Unknown",
        }));
        setVenueTypes(types);

        const venuesWithBranches = await Promise.all(
          response.map(async (v: any) => {
            const venueId = v.id || v._id || v.venue_id;
            let branches: any[] = [];
            try {
              if (venueId) {
                const res = await getAllBranches(String(venueId));
              const businesses = Array.isArray(res)
                ? res
                : Array.isArray(res?.businesses)
                ? res.businesses
                : Array.isArray(res?.data?.businesses)
                ? res.data.businesses
                : Array.isArray(res?.data)
                ? res.data
                : Array.isArray(res?.venue?.businesses)
                ? res.venue.businesses
                : [];
              branches = (businesses || []).map((b: any) => ({
                id: b.id || b._id,
                name: b.name,
                sender: b.sender || b.short_name || "",
                banner: b.banner || "",
                description: b.description || b.business_description || "",
                address: b.address,
                prior_cancel_hours: b.prior_cancel_hours || "",
                unit_phone: b.unit_phone || "",
                short_name: b.short_name || "",
                businessId: b.business_id || b.businessId || "",
                latitude: b.latitude,
                longitude: b.longitude,
                type_id: b.type || b.type_id || "",
                type_name: b.type || b.type_name || "",
                type: b.type || "",
                orderNumber: b.orderNumber ?? "",
              }));
            }
          } catch (e) {
            branches = [];
          }

            const bannerUrl = v.banner || "/logo192.png";
            const orderValue = v.order ?? v.orderNumber ?? v.order_number ?? "";

            return {
              id: venueId,
              orderNumber:
                typeof orderValue === "number"
                  ? orderValue
                  : orderValue !== "" && !Number.isNaN(Number(orderValue))
                  ? Number(orderValue)
                  : "",
              name: v.name || "Unknown Venue",
              image: bannerUrl,
              banner: v.banner || "",
              overLay: v.overLay || "",
              insta_url: v.insta_url || "",
              address: v.address || "",
              latitude: v.latitude || "",
              longitude: v.longitude || "",
              branches,
            };
          })
        );
        setVenues(venuesWithBranches);
      }
    } catch (e) {
      setVenueTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenuesWithBranches();
  }, [loadVenuesWithBranches]);

  const fetchBusinessTypes = async () => {
    try {
      const response = await getAllBusinessTypes();
      console.log("Business types response:", response);
      if (Array.isArray(response)) {
        const normalized = response
          .map((type: any) => {
            // Handle string array like ["FOOTBALL","PADEL","TENNIS"...]
            if (typeof type === "string") {
              return {
                id: type,
                name: type.replace(/_/g, " "),
              };
            }
            
            // Handle object array
            const id =
              type?.id ??
              type?.type_id ??
              type?._id ??
              type?.value ??
              type?.uuid;
            const name =
              type?.name ??
              type?.value ??
              type?.type_name ??
              type?.label ??
              type?.title ??
              type?.description;

            if (!id || !name) return null;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];

        const unique = Array.from(
          new Map(normalized.map((type) => [type.id, type])).values()
        ).sort((a, b) => a.name.localeCompare(b.name));

        setBusinessTypes(unique);
      } else {
        setBusinessTypes([]);
      }
    } catch (error) {
      console.error("Failed to fetch business types:", error);
      setBusinessTypes([]);
    }
  };

  React.useEffect(() => {
    fetchBusinessTypes();
  }, []);

  branchFields = [
    { type: "text", name: "name", label: "Branch Name", required: true },
    {
      type: "number",
      name: "orderNumber",
      label: "Order Number",
    },
    {
      type: "text",
      name: "short_name",
      label: "Short Name",
      required: true,
    },
    {
      name: "type",
      label: "Business Type",
      type: "dropdown",
      options: businessTypes.map((type) => ({
        label: type.name,
        value: type.id,
      })),
      required: true,
    },
    { type: "text", name: "description", label: "Description" },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => `+${c.phone}`),
    },
    {
      type: "text",
      name: "phoneNumber",
      label: "Phone Number",
    },

    {
      type: "text",
      name: "prior_cancel_hours",
      label: "Prior Cancellation Hours",
    },
    {
      name: "address",
      label: "Address",
      colSpan: 2,
      customRender: (
        value: any,
        onChange: (v: any) => void,
        helpers?: {
          setFieldValue: (name: string, value: any) => void;
          values?: Record<string, any>;
        }
      ) => {
        return (
          <AddressWithMapField value={value} onChange={onChange} helpers={helpers} />
        );
      },
    },
    { type: "file", name: "banner", label: "Branch Banner" },
  ];

  const handleEditVenue = (idx: number) => {
    setSelectedVenueIdx(idx);
    setVenueDialogOpen(true);
  };

  const openDeleteVenueDialog = (vIdx: number) => {
    setDeleteTarget({ type: "venue", vIdx });
    setDeleteDialogOpen(true);
  };
  const handleVenueDialogSubmit = async (values: Record<string, any>) => {
    if (selectedVenueIdx !== null) {
      const current = venues[selectedVenueIdx] as any;
      const venueId = current?.id || current?._id || current?.venue_id;
      const updatedVenues = [...venues];

      const currentBanner = current.banner || current.image;
      const rawBannerInput =
        values.banner !== undefined ? values.banner : values.image;

      if (
        rawBannerInput instanceof File &&
        rawBannerInput.size > MAX_IMAGE_SIZE_BYTES
      ) {
        toast.error("Image must be 2MB or smaller.");
        return;
      }

      const parsedOrderNumber =
        values.orderNumber !== undefined && values.orderNumber !== ""
          ? parseInt(String(values.orderNumber), 10)
          : undefined;

      let bannerValue = currentBanner;
      if (
        rawBannerInput &&
        typeof rawBannerInput === "object" &&
        rawBannerInput instanceof File
      ) {
        bannerValue = URL.createObjectURL(rawBannerInput);
      } else if (
        rawBannerInput &&
        typeof rawBannerInput === "string" &&
        rawBannerInput.trim() !== ""
      ) {
        bannerValue = rawBannerInput;
      }

      updatedVenues[selectedVenueIdx] = {
        ...updatedVenues[selectedVenueIdx],
        name: values.name,
        image: bannerValue,
        banner: bannerValue, // This will be used as banner
        overLay: values.overLay,
        insta_url: values.insta_url,
        address: values.address,
        latitude: values.latitude,
        longitude: values.longitude,
        orderNumber:
          parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)
            ? parsedOrderNumber
            : updatedVenues[selectedVenueIdx].orderNumber,
      } as any;
      setVenues(updatedVenues);

      if (venueId) {
        try {
          const payload: Record<string, any> = { name: values.name };
          if (
            parsedOrderNumber !== undefined &&
            !Number.isNaN(parsedOrderNumber)
          ) {
            payload.orderNumber = parsedOrderNumber;
            payload.order = parsedOrderNumber;
          }

          if (values.overLay !== undefined) {
            payload.overLay = values.overLay;
          }
          if (values.insta_url !== undefined) {
            payload.insta_url = values.insta_url;
          }
          if (values.address !== undefined) {
            payload.address = values.address;
          }
          if (values.latitude !== undefined) {
            payload.latitude = values.latitude;
          }
          if (values.longitude !== undefined) {
            payload.longitude = values.longitude;
          }

          if (
            rawBannerInput &&
            typeof rawBannerInput === "object" &&
            rawBannerInput instanceof File
          ) {
            payload.banner = rawBannerInput;
          } else if (
            typeof bannerValue === "string" &&
            bannerValue.trim() !== "" &&
            !bannerValue.startsWith("blob:")
          ) {
            payload.banner = bannerValue;
          } else if (
            typeof currentBanner === "string" &&
            currentBanner.trim() !== "" &&
            !currentBanner.startsWith("blob:")
          ) {
            payload.banner = currentBanner;
          }

          await updateBusiness(String(venueId), payload, { suppressToast: true });
          toast.success("Venue updated successfully");
          await loadVenuesWithBranches();
        } catch (e: any) {
          const msg =
            e?.response?.data?.error || e?.message || "Failed to update venue";
          showErrorToast(msg);
        }
      }
    }
    setVenueDialogOpen(false);
    setSelectedVenueIdx(null);
  };

  const handleEditBranch = (venueIdx: number, branchIdx: number) => {
    setSelectedVenueIdx(venueIdx);
    setSelectedBranchIdx(branchIdx);
    setBranchDialogOpen(true);
  };

  const openDeleteBranchDialog = (venueIdx: number, branchIdx: number) => {
    setDeleteTarget({ type: "branch", vIdx: venueIdx, bIdx: branchIdx });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogSubmit = async (values: Record<string, any>) => {
    try {
      if (!deleteTarget) return;
      if (deleteTarget.type === "venue") {
        const v = venues[deleteTarget.vIdx] as any;
        const venueId = v?.id || v?._id || v?.venue_id;
        if (venueId)
          await deleteVenue(
            String(venueId),
            { password: values.password },
            { suppressToast: true }
          );
        setVenues((prev) => prev.filter((_, i) => i !== deleteTarget.vIdx));
        toast.success("Venue deleted successfully");
      } else {
        const v = venues[deleteTarget.vIdx] as any;
        const b = v?.branches?.[deleteTarget.bIdx] as any;
        const branchId = b?.id || b?._id || b?.business_id;
        if (branchId)
          await deleteBranch(
            String(branchId),
            { password: values.password },
            { suppressToast: true }
          );
        setVenues((prev) =>
          prev.map((ven, i) => {
            if (i !== deleteTarget.vIdx) return ven;
            return {
              ...ven,
              branches: ven.branches.filter(
                (_: any, j: number) => j !== deleteTarget.bIdx
              ),
            };
          })
        );
        toast.success("Branch deleted successfully");
      }
    } catch (e) {
      const raw =
        (e as any)?.response?.data?.message ||
        (e as any)?.response?.data?.error ||
        (e as any)?.message;
      const msg = Array.isArray(raw) ? raw.join(", ") : raw || "Delete failed";
      toast.error(String(msg), { toastId: String(msg) });
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };
  const handleBranchDialogSubmit = async (values: Record<string, any>) => {
    if (selectedVenueIdx !== null && selectedBranchIdx !== null) {
      const v = venues[selectedVenueIdx] as any;
      const b = (v?.branches?.[selectedBranchIdx] as any) || {};
      const branchId = b?.id || b?._id || b?.business_id;
      const branchBannerInput = values.banner;

      if (
        branchBannerInput instanceof File &&
        branchBannerInput.size > MAX_IMAGE_SIZE_BYTES
      ) {
        toast.error("Banner must be 2MB or smaller.");
        return;
      }

      const fieldMapping: Record<string, string> = {
        name: "name",
        orderNumber: "orderNumber",
        short_name: "short_name",
        address: "address",
        fn_number: "fn_number",
        phoneNumber: "unit_phone",
        countryCode: "countryCode",
        prior_cancel_hours: "prior_cancel_hours",
        latitude: "latitude",
        longitude: "longitude",
        banner: "banner",
        businessId: "businessId",
        description: "description",
        type: "type",
      };

      let businessPhone = "";
      if (values.countryCode && values.phoneNumber) {
        const cc = String(values.countryCode).replace(/\s/g, "");
        businessPhone = `${cc}${values.phoneNumber}`;
      } else if (values.phoneNumber) {
        businessPhone = String(values.phoneNumber);
      }

      const parsedBranchOrder =
        values.orderNumber !== undefined && values.orderNumber !== ""
          ? parseInt(String(values.orderNumber), 10)
          : undefined;

      const payload: any = {};

      // businessId is now directly the ID (not the name)
      if (values.businessId) {
        payload.businessId = values.businessId;
      }

      // Handle type for business type
      if (values.type) {
        payload.type = values.type;
      }

      Object.keys(values).forEach((key) => {
        const backendKey = fieldMapping[key] || key;
        // Skip fields handled separately or not sent directly
        if (
          [
            "phoneNumber",
            "countryCode",
            "type",
            "businessId",
            "banner", // Skip banner as it's handled separately
          ].includes(key)
        )
          return;
        const val = values[key];
        // Allow clearing description by sending an empty string
        if (key === "description") {
          payload[backendKey] = val ?? "";
          return;
        }
        if (val !== undefined && val !== null && val !== "")
          payload[backendKey] = val;
      });

      // Include a plain description key for backward compatibility if needed
      if (values.description !== undefined && values.description !== null) {
        payload.description = values.description;
      }

      if (businessPhone) payload.unit_phone = businessPhone;
      if (
        parsedBranchOrder !== undefined &&
        !Number.isNaN(parsedBranchOrder)
      ) {
        payload.orderNumber = parsedBranchOrder;
      }

      const updatedVenues = [...venues] as any[];
      updatedVenues[selectedVenueIdx].branches[selectedBranchIdx] = {
        ...b,
        name: values.name,
        description: values.description,
        address: values.address,
        prior_cancel_hours: values.prior_cancel_hours,
        unit_phone: businessPhone || b.unit_phone,
        short_name: values.short_name,
        type: values.type,
        latitude: values.latitude,
        longitude: values.longitude,
        businessId: values.businessId,
        orderNumber:
          parsedBranchOrder !== undefined && !Number.isNaN(parsedBranchOrder)
            ? parsedBranchOrder
            : b.orderNumber,
      };
      setVenues(updatedVenues as any);

      if (branchId) {
        try {
          const hasFile =
            values.banner &&
            typeof values.banner === "object" &&
            values.banner instanceof File;
          
          if (hasFile) {
            // Create FormData for file upload
            const formData = new FormData();
            
            // Add all payload fields to FormData
            Object.entries(payload).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                formData.append(key, String(value));
              }
            });
            
            // Add the banner file
            formData.append("banner", values.banner as File);
            
            await updateBranch(String(branchId), formData, { suppressToast: true });
          } else {
            // Send as JSON payload when no file
            await updateBranch(String(branchId), payload, { suppressToast: true });
          }
          
          toast.success("Branch updated successfully");
          
          // Refresh branch data from server
          try {
            const branchesResp = await getAllBranches(String(v.id || v._id || v.venue_id));
            const businesses = Array.isArray(branchesResp)
              ? branchesResp
              : Array.isArray(branchesResp?.businesses)
              ? branchesResp.businesses
              : Array.isArray(branchesResp?.data?.businesses)
              ? branchesResp.data.businesses
              : Array.isArray(branchesResp?.data)
              ? branchesResp.data
              : Array.isArray(branchesResp?.venue?.businesses)
              ? branchesResp.venue.businesses
              : [];

            const branchList = (businesses || []).map((b: any) => ({
              id: b.id || b._id,
              name: b.name,
              sender: b.sender || b.short_name || "",
              banner: b.banner || "",
              description: b.description || b.business_description || "",
              address: b.address,
              prior_cancel_hours: b.prior_cancel_hours || "",
              unit_phone: b.unit_phone || "",
              short_name: b.short_name || "",
              businessId: b.business_id || b.businessId || "",
              latitude: b.latitude,
              longitude: b.longitude,
              type_id: b.type || b.type_id || "",
              type_name: b.type || b.type_name || "",
              type: b.type || "",
              orderNumber: b.orderNumber ?? "",
            }));

            setVenues((prev) =>
              prev.map((pv, i) =>
                i === selectedVenueIdx ? { ...pv, branches: branchList } : pv
              )
            );
          } catch (e) {
            // best effort only — ignore refresh errors
          }
        } catch (e: any) {
          const msg =
            e?.response?.data?.error || 
            e?.response?.data?.message || 
            e?.message || 
            "Failed to update branch";
          showErrorToast(msg);
          console.error("Branch update error:", e);
        }
      }
    }
    setBranchDialogOpen(false);
    setSelectedBranchIdx(null);
    setSelectedVenueIdx(null);
  };

  const iconAction = (danger = false) => ({
    width: 34,
    height: 34,
    color: danger ? "error.main" : COLORS.MainColor,
    bgcolor: danger ? "rgba(211, 47, 47, 0.06)" : alpha(COLORS.MainColor, 0.06),
    "&:hover": {
      bgcolor: danger ? "rgba(211, 47, 47, 0.14)" : alpha(COLORS.MainColor, 0.14),
    },
  });
  const formatType = (type?: string) =>
    String(type || "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.MainBackground }}>
    <Box sx={{ maxWidth: 1360, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 5 } }}>
      <PageHeader
        title="Edit Venues"
        subtitle="Update your venues and the branches inside them."
        icon={<EditLocationAltOutlinedIcon />}
        sx={{ mb: 3 }}
      />
      <Box
        sx={{
          display: "grid",
          gap: { xs: 2.5, sm: 3.5 },
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
        }}
      >
        {loading && (!venues || venues.length === 0)
          ? Array.from({ length: 3 }).map((_, i) => (
              <Paper key={i} elevation={0} sx={venueCardSx}>
                <Skeleton variant="rectangular" animation="wave" height={200} />
                <Box sx={{ p: 2.5 }}>
                  <Skeleton width="55%" height={28} />
                  <Skeleton width="80%" />
                  <Skeleton variant="rounded" height={56} sx={{ mt: 2 }} />
                  <Skeleton variant="rounded" height={56} sx={{ mt: 1 }} />
                </Box>
              </Paper>
            ))
          : null}
        {!loading && venues.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No venues to edit yet.
          </Typography>
        )}
        {venues.map((venue, vIdx) => (
          <Paper
            key={venue.name + vIdx}
            elevation={0}
            sx={{
              ...venueCardSx,
              "@keyframes editVenueIn": {
                from: { opacity: 0, transform: "translateY(10px)" },
                to: { opacity: 1, transform: "none" },
              },
              animation: "editVenueIn 0.4s cubic-bezier(0.2, 0, 0, 1) both",
              animationDelay: `${Math.min(vIdx, 8) * 50}ms`,
              "&:hover": { boxShadow: "0 12px 28px rgba(15, 23, 42, 0.10)" },
            }}
          >
            <VenueCover
              src={venue.banner || venue.image}
              name={venue.name}
              sx={{ height: 200 }}
            />
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.25rem",
                      color: COLORS.typographyColor,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {venue.name}
                  </Typography>
                  {venue.address && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "text.secondary",
                        mt: 0.25,
                        minWidth: 0,
                      }}
                    >
                      <PlaceOutlinedIcon sx={{ fontSize: "1rem", flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {venue.address}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Tooltip title="Edit venue">
                  <IconButton
                    aria-label="Edit venue"
                    onClick={() => handleEditVenue(vIdx)}
                    sx={iconAction()}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete venue">
                  <IconButton
                    aria-label="Delete venue"
                    onClick={() => openDeleteVenueDialog(vIdx)}
                    sx={iconAction(true)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2.5,
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}
                >
                  Branches
                </Typography>
                <Chip
                  label={venue.branches.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    bgcolor: alpha(COLORS.MainColor, 0.1),
                    color: COLORS.MainColor,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {venue.branches.length === 0 && (
                  <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", py: 1 }}>
                    No branches in this venue yet.
                  </Typography>
                )}
                {venue.branches.map((branch: any, bIdx: number) => (
                  <Box
                    key={branch.name + bIdx}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "background-color 0.2s, border-color 0.2s",
                      "&:hover": {
                        bgcolor: alpha(COLORS.MainColor, 0.03),
                        borderColor: alpha(COLORS.MainColor, 0.25),
                      },
                    }}
                  >
                    <VenueCover
                      src={branch.banner}
                      name={branch.name || ""}
                      initialsSize="0.85rem"
                      sx={{ width: 42, height: 42, borderRadius: 2, flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.92rem",
                            color: COLORS.typographyColor,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {branch.name}
                        </Typography>
                        {(branch.type_name || branch.type) && (
                          <Chip
                            label={formatType(branch.type_name || branch.type)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.68rem", flexShrink: 0 }}
                          />
                        )}
                      </Box>
                      {branch.description && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {branch.description}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Edit branch">
                      <IconButton
                        aria-label="Edit branch"
                        onClick={() => handleEditBranch(vIdx, bIdx)}
                        sx={iconAction()}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete branch">
                      <IconButton
                        aria-label="Delete branch"
                        onClick={() => openDeleteBranchDialog(vIdx, bIdx)}
                        sx={iconAction(true)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
      <GenericFormDialog
        open={venueDialogOpen}
        title="Edit Venue"
        onClose={() => setVenueDialogOpen(false)}
        onSubmit={handleVenueDialogSubmit}
        fields={venueFields}
        initialValues={
          selectedVenueIdx !== null
            ? {
                name: venues[selectedVenueIdx].name,
                orderNumber:
                  venues[selectedVenueIdx].orderNumber !== undefined &&
                  venues[selectedVenueIdx].orderNumber !== null
                    ? venues[selectedVenueIdx].orderNumber
                    : "",
                overLay: (venues[selectedVenueIdx] as any)?.overLay || "",
                insta_url: (venues[selectedVenueIdx] as any)?.insta_url || "",
                banner:
                  venues[selectedVenueIdx].banner ||
                  venues[selectedVenueIdx].image,
                address: (venues[selectedVenueIdx] as any)?.address || "",
                latitude: (venues[selectedVenueIdx] as any)?.latitude || "",
                longitude: (venues[selectedVenueIdx] as any)?.longitude || "",
              }
            : {}
        }
      />
      <GenericFormDialog
        open={branchDialogOpen}
        title="Edit Branch"
        onClose={() => setBranchDialogOpen(false)}
        onSubmit={handleBranchDialogSubmit}
        fields={branchFields}
        initialValues={
          selectedVenueIdx !== null && selectedBranchIdx !== null
            ? {
                name:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.name || "",
                short_name:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.short_name || "",
                type: (() => {
                  const br = (venues[selectedVenueIdx].branches[selectedBranchIdx] as any) || {};
                  return br.type || br.type_id || br.type_name || "";
                })(),
                businessId: (() => {
                  const br =
                    (venues[selectedVenueIdx].branches[
                      selectedBranchIdx
                    ] as any) || {};
                  return String(br.businessId || br.business_id || "");
                })(),
                description:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.description ||
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.business_description ||
                  "",
                address:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.address || "",
                prior_cancel_hours:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.prior_cancel_hours || "",
                countryCode: (() => {
                  const p =
                    (
                      venues[selectedVenueIdx].branches[
                        selectedBranchIdx
                      ] as any
                    )?.unit_phone || "";
                  const match = String(p).match(/^(\+\d{1,3})/);
                  return match ? match[1] : "+961";
                })(),
                phoneNumber: (() => {
                  const p =
                    (
                      venues[selectedVenueIdx].branches[
                        selectedBranchIdx
                      ] as any
                    )?.unit_phone || "";
                  return String(p).replace(/^(\+\d{1,3})/, "");
                })(),
                latitude:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.latitude || "",
                longitude:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.longitude || "",
                orderNumber:
                  (venues[selectedVenueIdx].branches[selectedBranchIdx] as any)
                    ?.orderNumber ?? "",
              }
            : {}
        }
      />
      {/* Delete confirmation dialog */}
      <GenericFormDialog
        open={deleteDialogOpen}
        title={
          deleteTarget?.type === "venue" ? "Delete Venue" : "Delete Branch"
        }
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onSubmit={handleDeleteDialogSubmit}
        fields={deleteFields}
        submitLabel="Delete"
        initialValues={((): Record<string, string> => {
          if (!deleteTarget) return { name: "", password: "" };
          if (deleteTarget.type === "venue") {
            const v = venues[deleteTarget.vIdx];
            return { name: v?.name || "", password: "" };
          }
          const b = venues[deleteTarget.vIdx]?.branches?.[deleteTarget.bIdx];
          return { name: b?.name || "", password: "" };
        })()}
      />
    </Box>
    </Box>
  );
};

const venueCardSx = {
  borderRadius: 4,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
  bgcolor: COLORS.WhiteBackground,
  transition: "box-shadow 0.25s",
};

export default EditVenues;
